import React, { useMemo, useState } from 'react';
import { format, differenceInCalendarDays } from 'date-fns';
import { usePrestamos } from '../../context/PrestamosContext';
import { Prestamo } from '../../types';
import {
  Plus, Search, Package, ArrowUpRight, ArrowDownLeft, Check, RotateCcw,
  Edit2, Trash2, AlertTriangle, X, Sparkles,
} from 'lucide-react';
import { ModalPrestamo } from '../../components/prestamos/ModalPrestamo';
import { useConfirmar } from '../../context/ConfirmarContext';
import { usePerfil } from '../../context/PerfilContext';
import { usePlus } from '../../context/PlusContext';
import { estaVencido } from '../../utils/prestamosVencidos';

type FiltroEstado = 'fuera' | 'devueltos' | 'todos';
type FiltroDireccion = 'todos' | 'prestado' | 'recibido';

const fecha = (d: Date) => format(d, 'dd/MM/yyyy');

const haceDias = (d: Date) => {
  const n = differenceInCalendarDays(new Date(), d);
  return n <= 0 ? 'hoy' : n === 1 ? 'ayer' : `hace ${n} días`;
};

const vencido = (p: Prestamo) => estaVencido(p);

const chip = (activo: boolean) =>
  `px-3 py-1.5 text-xs font-semibold rounded-lg border transition ${
    activo
      ? 'bg-cyan-600 border-cyan-600 text-white'
      : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-gray-400'
  }`;

export const Prestamos: React.FC = () => {
  const confirmar = useConfirmar();
  const { prestamos, loadingPrestamos, marcarDevuelto, eliminarPrestamo } = usePrestamos();
  // Préstamos es de Plus. Sin él se ve todo y se puede marcar como devuelto
  // (que nadie se quede con algo pendiente que no puede cerrar), nada más.
  const { puedeEscribirPrestamos: puede } = usePerfil();
  const { ofrecerPlus } = usePlus();

  const [estado, setEstado] = useState<FiltroEstado>('fuera');
  const [direccion, setDireccion] = useState<FiltroDireccion>('todos');
  const [persona, setPersona] = useState<string | null>(null);
  const [busqueda, setBusqueda] = useState('');
  const [modalAbierto, setModalAbierto] = useState(false);
  const [prestamoEditar, setPrestamoEditar] = useState<Prestamo | undefined>();

  const fuera = useMemo(() => prestamos.filter(p => !p.fechaDevolucion), [prestamos]);
  const vencidos = fuera.filter(vencido).length;

  // Quién tiene cosas tuyas, y de quién tienes tú, ahora mismo
  const porPersona = useMemo(() => {
    const mapa = new Map<string, { persona: string; tiene: number; teDeja: number }>();
    fuera.forEach(p => {
      const clave = p.persona.trim().toLowerCase();
      const r = mapa.get(clave) ?? { persona: p.persona.trim(), tiene: 0, teDeja: 0 };
      if (p.direccion === 'prestado') r.tiene += 1; else r.teDeja += 1;
      mapa.set(clave, r);
    });
    return Array.from(mapa.values()).sort((a, b) => a.persona.localeCompare(b.persona, 'es'));
  }, [fuera]);

  const lista = useMemo(() => {
    const t = busqueda.trim().toLowerCase();
    return prestamos
      .filter(p => estado === 'todos' || (estado === 'fuera' ? !p.fechaDevolucion : !!p.fechaDevolucion))
      .filter(p => direccion === 'todos' || p.direccion === direccion)
      .filter(p => !persona || p.persona.trim().toLowerCase() === persona.toLowerCase())
      .filter(p => !t || [p.objeto, p.persona, p.notas].some(v => v?.toLowerCase().includes(t)))
      // Los vencidos, arriba
      .sort((a, b) => Number(vencido(b)) - Number(vencido(a)));
  }, [prestamos, estado, direccion, persona, busqueda]);

  const abrirNuevo = () => {
    if (!puede) { ofrecerPlus('prestamos'); return; }
    setPrestamoEditar(undefined); setModalAbierto(true);
  };
  const abrirEditar = (p: Prestamo) => { setPrestamoEditar(p); setModalAbierto(true); };

  const borrar = async (p: Prestamo) => {
    if (!(await confirmar({
      titulo: `¿Borrar el préstamo de «${p.objeto}»?`,
      mensaje: 'No se puede deshacer. Si ya ha vuelto, mejor márcalo como devuelto y así queda en el historial.',
      accion: 'Borrar',
    }))) return;
    await eliminarPrestamo(p.id);
  };

  if (loadingPrestamos) {
    return <div className="max-w-5xl mx-auto px-4 py-10 text-gray-500 dark:text-gray-400">Cargando…</div>;
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">

      <header className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Préstamos</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
            {fuera.length} objeto{fuera.length === 1 ? '' : 's'} fuera
            {vencidos > 0 && (
              <span className="text-red-600 dark:text-red-400 font-semibold">
                {' '}· {vencidos} pasado{vencidos === 1 ? '' : 's'} de fecha
              </span>
            )}
          </p>
        </div>
        <button onClick={abrirNuevo}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg
                     bg-cyan-600 hover:bg-cyan-700 text-white transition">
          {puede ? <Plus className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />} Nuevo préstamo
        </button>
      </header>

      {!puede && (
        <button type="button" onClick={() => ofrecerPlus('prestamos')}
          className="w-full mb-4 flex items-center gap-3 p-3 rounded-xl border border-amber-200 dark:border-amber-800
                     bg-amber-50 dark:bg-amber-900/20 text-left text-sm text-amber-900 dark:text-amber-200">
          <Sparkles className="w-5 h-5 shrink-0 text-amber-600 dark:text-amber-400" />
          <span>
            <strong>Préstamos es de myarinn Plus.</strong>{' '}
            {prestamos.length > 0
              ? 'Puedes ver los que tienes y marcarlos como devueltos; para anotar nuevos o cambiarlos, hazte Plus.'
              : 'Apunta qué dejas, a quién y cuándo tiene que volver. Toca para ver Plus.'}
          </span>
        </button>
      )}

      {/* ── Resumen por persona ───────────────────────────────────────────── */}
      {porPersona.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {porPersona.map(r => {
            const activa = persona?.toLowerCase() === r.persona.toLowerCase();
            const partes = [
              r.tiene > 0 && `tiene ${r.tiene} tuyo${r.tiene === 1 ? '' : 's'}`,
              r.teDeja > 0 && `te deja ${r.teDeja}`,
            ].filter(Boolean).join(' · ');
            return (
              <button key={r.persona} onClick={() => setPersona(activa ? null : r.persona)}
                aria-pressed={activa}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border transition ${
                  activa
                    ? 'bg-cyan-600 border-cyan-600 text-white'
                    : 'bg-cyan-50 dark:bg-cyan-900/30 border-cyan-200 dark:border-cyan-800 text-cyan-800 dark:text-cyan-300 hover:border-cyan-400'
                }`}>
                <span className="font-semibold">{r.persona}</span>
                <span className="opacity-80">{partes}</span>
                {activa && <X className="w-3 h-3" />}
              </button>
            );
          })}
        </div>
      )}

      {/* ── Filtros ───────────────────────────────────────────────────────── */}
      {prestamos.length > 0 && (
        <div className="flex flex-wrap gap-2 items-center mb-4">
          <div className="flex gap-1">
            {([['fuera', 'Fuera'], ['devueltos', 'Devueltos'], ['todos', 'Todos']] as const).map(([v, l]) => (
              <button key={v} onClick={() => setEstado(v)} className={chip(estado === v)}>{l}</button>
            ))}
          </div>
          <div className="flex gap-1">
            {([['todos', 'Todos'], ['prestado', 'Dejados'], ['recibido', 'Recibidos']] as const).map(([v, l]) => (
              <button key={v} onClick={() => setDireccion(v)} className={chip(direccion === v)}>{l}</button>
            ))}
          </div>
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input type="search" value={busqueda} onChange={e => setBusqueda(e.target.value)}
              placeholder="Buscar objeto, persona o nota…"
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600
                         bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm
                         focus:outline-none focus:ring-2 focus:ring-cyan-500" />
          </div>
        </div>
      )}

      {/* ── Lista ─────────────────────────────────────────────────────────── */}
      {lista.length === 0 ? (
        <div className="text-center py-16 bg-white dark:bg-gray-800 rounded-xl border border-dashed border-gray-300 dark:border-gray-700">
          <Package className="w-10 h-10 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
          <p className="text-gray-600 dark:text-gray-400 font-medium">
            {prestamos.length === 0
              ? 'Todavía no has anotado ningún préstamo.'
              : estado === 'fuera' && !busqueda && !persona && direccion === 'todos'
                ? 'No tienes nada fuera. Todo ha vuelto a casa.'
                : 'Ningún préstamo coincide con los filtros.'}
          </p>
          {prestamos.length === 0 && (
            <>
              <p className="text-sm text-gray-500 dark:text-gray-500 mt-1 max-w-sm mx-auto">
                Apunta qué dejas, a quién y cuándo, y sabrás siempre qué falta por volver.
              </p>
              <button onClick={abrirNuevo}
                className="inline-flex items-center gap-1.5 mt-4 px-4 py-2 text-sm font-semibold rounded-lg
                           bg-cyan-600 hover:bg-cyan-700 text-white transition">
                <Plus className="w-4 h-4" /> Anotar el primero
              </button>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {lista.map(p => {
            const devuelto = !!p.fechaDevolucion;
            const pasado = vencido(p);
            const Icono = p.direccion === 'prestado' ? ArrowUpRight : ArrowDownLeft;
            return (
              <div key={p.id}
                className={`bg-white dark:bg-gray-800 rounded-xl border p-4 transition ${
                  pasado
                    ? 'border-red-300 dark:border-red-800'
                    : devuelto
                      ? 'border-dashed border-gray-300 dark:border-gray-700 opacity-70'
                      : 'border-gray-200 dark:border-gray-700'
                }`}>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 shrink-0 rounded-lg bg-cyan-50 dark:bg-cyan-900/30
                                  flex items-center justify-center"
                    title={p.direccion === 'prestado' ? 'Lo has dejado tú' : 'Te lo han dejado'}>
                    <Icono className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
                  </div>

                  <div className="min-w-0 flex-1">
                    <h2 className="font-bold text-gray-900 dark:text-white truncate">
                      {p.objeto}
                      {pasado && (
                        <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide
                                         text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/40 px-2 py-0.5 rounded-full align-middle">
                          <AlertTriangle className="w-3 h-3" /> Pasado de fecha
                        </span>
                      )}
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      {p.direccion === 'prestado' ? 'Lo tiene ' : 'Te lo dejó '}
                      <span className="font-semibold">{p.persona}</span>
                    </p>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      <span>Desde el {fecha(p.fechaPrestamo)}{!devuelto && ` (${haceDias(p.fechaPrestamo)})`}</span>
                      {p.fechaPrevista && !devuelto && (
                        <span className={pasado ? 'text-red-600 dark:text-red-400 font-semibold' : ''}>
                          Vuelve antes del {fecha(p.fechaPrevista)}
                        </span>
                      )}
                      {devuelto && <span className="text-green-700 dark:text-green-400">Devuelto el {fecha(p.fechaDevolucion!)}</span>}
                    </div>
                    {p.notas && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 whitespace-pre-line">{p.notas}</p>
                    )}
                  </div>

                  <div className="flex items-center gap-0.5 shrink-0">
                    {devuelto ? (
                      <button onClick={() => marcarDevuelto(p.id, null)} title="Volver a dar por fuera"
                        className="p-2 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200
                                   hover:bg-gray-100 dark:hover:bg-gray-700 transition">
                        <RotateCcw className="w-4 h-4" />
                      </button>
                    ) : (
                      <button onClick={() => marcarDevuelto(p.id, new Date())} title="Marcar como devuelto hoy"
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold
                                   text-green-700 dark:text-green-400 border border-green-300 dark:border-green-700
                                   hover:bg-green-50 dark:hover:bg-green-900/30 transition">
                        <Check className="w-3.5 h-3.5" /> Devuelto
                      </button>
                    )}
                    {puede && (
                      <>
                        <button onClick={() => abrirEditar(p)} title="Editar"
                          className="p-2 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200
                                     hover:bg-gray-100 dark:hover:bg-gray-700 transition">
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => borrar(p)} title="Borrar"
                          className="p-2 rounded-lg text-gray-400 hover:text-red-600
                                     hover:bg-red-50 dark:hover:bg-red-900/20 transition">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {modalAbierto && (
        <ModalPrestamo prestamo={prestamoEditar} onClose={() => setModalAbierto(false)} />
      )}
    </div>
  );
};
